// Plugin registration diagnostics.
//
// IMPORTANT: Capacitor.isPluginAvailable() returns false on iOS for several
// pods that auto-register via CAPBridgedPlugin (the modern pattern used by
// @capgo/* and many community plugins), even when the plugin works fine at
// call time. So we treat the actual call as the source of truth: try it,
// then classify the failure mode. Registry membership is only a hint used
// when the caller has no probe call to run.
import { Capacitor } from "@capacitor/core";

export type PluginProbeState =
  | "not_registered"     // Probe call rejected with UNIMPLEMENTED — plugin really isn't in this build
  | "registered_ok"      // Probe call resolved
  | "registered_errored" // Probe call rejected with a real error (auth/race/etc.)
  | "unknown";           // Web, or no probe call provided and registry is silent

export type PluginProbeResult = {
  name: string;
  /** Human-readable method that was probed (e.g. "getAuthStatus()"). */
  probedMethod?: string;
  state: PluginProbeState;
  errorMessage?: string;
};

/** Hint only — DO NOT treat a `false` result as proof the plugin is missing on iOS. */
export function isPluginRegistered(name: string): boolean {
  try {
    return Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
}

const UNIMPLEMENTED_RE = /UNIMPLEMENTED|not implemented|is not a function|no such method/i;

/**
 * Probe a plugin by attempting a real call. The result of the call is the
 * verdict — registry membership is ignored when a probe call is provided.
 */
export async function probePlugin(
  name: string,
  probeCall?: () => Promise<unknown>,
  probedMethod?: string,
): Promise<PluginProbeResult> {
  if (!Capacitor.isNativePlatform()) {
    return { name, probedMethod, state: "unknown" };
  }

  if (probeCall) {
    try {
      await probeCall();
      return { name, probedMethod, state: "registered_ok" };
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      return {
        name,
        probedMethod,
        state: UNIMPLEMENTED_RE.test(msg) ? "not_registered" : "registered_errored",
        errorMessage: msg,
      };
    }
  }

  // No probe call — fall back to the registry hint, but never claim "missing"
  // based on it alone (too many false negatives on iOS).
  return {
    name,
    probedMethod,
    state: isPluginRegistered(name) ? "registered_ok" : "unknown",
  };
}
